import React, { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from 'react-query'
import { toast } from 'sonner'

import { PageHeader, StatCardGroup, StatCard } from '@/components/shared'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { blogService } from '@/services/blogService'
import { BlogToolbar } from '@/components/AdminPanel/Blog/BlogToolbar'
import { BlogPostList } from '@/components/AdminPanel/Blog/BlogPostList'
import { FileText, CheckCircle2, FileEdit, Archive } from 'lucide-react'

function postMatchesSearch(post, query) {
  const value = query.trim().toLowerCase()
  if (!value) return true
  return [post.title, post.category, post.excerpt]
    .filter(Boolean)
    .some((field) => String(field).toLowerCase().includes(value))
}

const BlogManager = () => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [duplicateTarget, setDuplicateTarget] = useState(null)
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false)
  const [duplicating, setDuplicating] = useState(false)
  const [archiveTarget, setArchiveTarget] = useState(null)
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false)
  const [archiving, setArchiving] = useState(false)

  const { data, isLoading } = useQuery('blogs', blogService.getAllBlogs, {
    onError: (err) => toast.error('Failed to fetch blog posts: ' + (err.response?.data?.error || err.message))
  })

  const posts = data?.data?.posts || []
  const filteredPosts = useMemo(
    () => posts
      .filter((post) => postMatchesSearch(post, searchQuery))
      .filter((post) => {
        if (statusFilter === 'archived') return post.is_archived
        if (statusFilter === 'published') return !post.is_archived && post.published
        if (statusFilter === 'draft') return !post.is_archived && !post.published
        return true
      }),
    [posts, searchQuery, statusFilter]
  )

  const counts = useMemo(() => {
    const active = posts.filter((post) => !post.is_archived)
    return {
      total: posts.length,
      published: active.filter((post) => post.published).length,
      drafts: active.filter((post) => !post.published).length,
      archived: posts.filter((post) => post.is_archived).length,
    }
  }, [posts])

  function nextSlugCandidate(baseId, existingIds) {
    let candidate = `${baseId}-copy`
    let suffix = 2
    while (existingIds.has(candidate)) {
      candidate = `${baseId}-copy-${suffix}`
      suffix += 1
    }
    return candidate
  }

  async function handleDuplicate() {
    if (!duplicateTarget) return
    setDuplicating(true)
    try {
      const {
        author, authorProfile, created_at, updated_at, ...rest
      } = duplicateTarget

      // Re-fetch instead of trusting the (possibly stale) cached list, since
      // slug uniqueness has to match what the server actually has right now.
      const freshData = await queryClient.fetchQuery('blogs', blogService.getAllBlogs)
      const existingIds = new Set((freshData?.data?.posts || []).map((post) => post.id))

      let candidate = nextSlugCandidate(duplicateTarget.id, existingIds)
      const maxAttempts = 5
      for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
        const duplicate = {
          ...rest,
          id: candidate,
          title: `${duplicateTarget.title} (Copy)`,
          published: false,
          published_date: new Date().toISOString().slice(0, 10),
        }
        try {
          await blogService.createBlog(duplicate)
          break
        } catch (err) {
          const isSlugConflict = err.response?.status === 400 && /slug/i.test(err.response?.data?.error || '')
          if (!isSlugConflict || attempt === maxAttempts - 1) throw err
          existingIds.add(candidate)
          candidate = nextSlugCandidate(duplicateTarget.id, existingIds)
        }
      }

      toast.success('Blog post duplicated')
      queryClient.invalidateQueries('blogs')
      setDuplicateDialogOpen(false)
    } catch (err) {
      toast.error('Failed to duplicate blog post: ' + (err.response?.data?.error || err.message))
    } finally {
      setDuplicating(false)
    }
  }

  async function handleArchive() {
    if (!archiveTarget) return
    setArchiving(true)
    try {
      await blogService.archiveBlog(archiveTarget.id)
      toast.success('Blog post archived')
      queryClient.invalidateQueries('blogs')
      setArchiveDialogOpen(false)
    } catch (err) {
      toast.error('Failed to archive blog post: ' + (err.response?.data?.error || err.message))
    } finally {
      setArchiving(false)
    }
  }

  async function handleRestore(post) {
    try {
      await blogService.restoreBlog(post.id)
      toast.success('Blog post restored')
      queryClient.invalidateQueries('blogs')
    } catch (err) {
      toast.error('Failed to restore blog post: ' + (err.response?.data?.error || err.message))
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Blog Posts"
        description="Manage blog content for the company website"
        actions={
          <BlogToolbar
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            onClearSearch={() => setSearchQuery('')}
            statusFilter={statusFilter}
            onStatusFilterChange={setStatusFilter}
            onRefresh={() => queryClient.invalidateQueries('blogs')}
            onNewPost={() => navigate('/admin-panel/blogs/new')}
          />
        }
      />

      <StatCardGroup>
        <StatCard title="Total Posts" value={counts.total} description="All blog posts" icon={FileText} contentClassName="p-4" />
        <StatCard title="Published" value={counts.published} description="Live posts" icon={CheckCircle2} valueClassName="text-emerald-600" contentClassName="p-4" />
        <StatCard title="Drafts" value={counts.drafts} description="Saved but hidden" icon={FileEdit} valueClassName="text-yellow-600" contentClassName="p-4" />
        <StatCard title="Archived" value={counts.archived} description="Hidden & recoverable" icon={Archive} valueClassName="text-muted-foreground" contentClassName="p-4" />
      </StatCardGroup>

      <BlogPostList
        posts={filteredPosts}
        loading={isLoading}
        emptyMessage={searchQuery ? `No posts match "${searchQuery}".` : 'No blog posts yet.'}
        onEdit={(post) => navigate(`/admin-panel/blogs/edit/${encodeURIComponent(post.id)}`)}
        onDuplicate={(post) => { setDuplicateTarget(post); setDuplicateDialogOpen(true) }}
        onArchive={(post) => { setArchiveTarget(post); setArchiveDialogOpen(true) }}
        onRestore={handleRestore}
      />

      <AlertDialog open={duplicateDialogOpen} onOpenChange={setDuplicateDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Duplicate this blog post?</AlertDialogTitle>
            <AlertDialogDescription>
              A new draft copy of "{duplicateTarget?.title}" will be created. You can edit and publish it separately.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={duplicating}>Cancel</AlertDialogCancel>
            <AlertDialogAction disabled={duplicating} onClick={(event) => { event.preventDefault(); handleDuplicate() }}>
              {duplicating ? 'Duplicating...' : 'Duplicate'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={archiveDialogOpen} onOpenChange={setArchiveDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archive this blog post?</AlertDialogTitle>
            <AlertDialogDescription>
              "{archiveTarget?.title}" will be hidden from the blog list and the public site. You can restore it anytime from the Archived filter.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={archiving}>Cancel</AlertDialogCancel>
            <AlertDialogAction disabled={archiving} onClick={(event) => { event.preventDefault(); handleArchive() }}>
              {archiving ? 'Archiving...' : 'Archive'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

export default BlogManager
